import React, { useEffect, useState } from "react";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import { TeamsWithColours } from "../RoundResultsPage";
import ResultsBarChart from "../charts/ResultsBarChart";
import {
  percentage,
  round,
  thousandSeparator,
} from "../../model/utils/formatters";
import ChartCard from "../components/ChartCard";
import { getYAxisDataV2 } from "../charts/yaxis";
import RoundResultsContainer from "../components/RoundsResultsContainer";

interface Props {
  currencyDenomination: string;
  teams: TeamsWithColours[];
  npatRoe: ModelAPI.Reporting.NpatAndRoe[];
}

const createTopLabel = (val: any) => {
  const TopLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value == null) {
      return null;
    }
    return (
      <g>
        <text
          x={x + width / 2}
          y={
            (value < 0 ? y + height : y) +
            (value < 0 ? -10 : value < 200 ? -15 : -5)
          }
          fill="#000"
          textAnchor="middle"
          fontWeight="bold"
          fontSize="clamp(0.25rem, calc(0.25rem + 0.80vw), 4rem)"
        >
          {val}
        </text>
      </g>
    );
  };
  return TopLabel;
};

const ResponsiveRectangle = (props: any) => {
  const [dimensions, setDimensions] = useState({
    width: 40,
    height: 20,
    offsetX: -20,
    offsetY: -15,
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (window.matchMedia("(min-width: 3840px)").matches) {
        setDimensions({ width: 200, height: 60, offsetX: -70, offsetY: -40 });
      } else if (window.matchMedia("(min-width: 1200px)").matches) {
        setDimensions({ width: 60, height: 30, offsetX: -30, offsetY: -20 });
      } else {
        setDimensions({ width: 42, height: 20, offsetX: -21, offsetY: -14 });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const { x, y, width, height } = props;
  const { width: rectWidth, height: rectHeight, offsetX, offsetY } = dimensions;

  return (
    <rect
      x={x + width / 2 + offsetX}
      y={y + height / 2 + offsetY}
      width={rectWidth}
      height={rectHeight}
      fill="#fff"
    />
  );
};
const createMiddleLabel = (val: any) => {
  const MiddleLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value == null) {
      return null;
    }
    return (
      <g>
        <ResponsiveRectangle x={x} y={y} width={width} height={height} />
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#000"
          textAnchor="middle"
          fontWeight="bold"
          fontSize="clamp(0.25rem, calc(0.25rem + 0.80vw), 2rem)"
        >
          {val}
        </text>
      </g>
    );
  };
  return MiddleLabel;
};

const RoundResultsNPATRoe: React.FC<Props> = ({
  currencyDenomination,
  teams,
  npatRoe,
}) => {
  const data = npatRoe.map((teamValue) => {
    const team = teams.find((t) => t.teamId === teamValue.teamId);
    if (team == null) {
      throw new Error("Team should exist");
    }
    const result: any = {
      name: team.teamName,
      [`team${teamValue.teamId}`]: teamValue.npat,
      key: `team${teamValue.teamId}`,
      colour: team.colour,
      formatter: (val: number, params: any) => {
        if (params?.position === "top") {
          return thousandSeparator(round(teamValue.npat, 0));
        } else if (params?.position === "middle") {
          return percentage(teamValue.roe);
        }
        return "";
      },
      topLabel: createTopLabel(thousandSeparator(round(teamValue.npat, 0))),
      content: createMiddleLabel(percentage(teamValue.roe)),
      hasMiddleLabel: true,
    };
    for (const team of teams) {
      if (team.teamId !== teamValue.teamId) {
        result[`team${team.teamId}`] = null;
      }
    }
    return result;
  });

  const { minValue, maxValue, numberOfTicks } = getYAxisDataV2({
    pastRound1: true,
    min: 0,
    data: [{ name: "fake0", value: 0 }].concat(
      npatRoe.map((np) => ({ name: "dddd", value: np.npat })),
    ),
  });

  return (
    <RoundResultsContainer>
      <VerticalGroup full center wide>
        <ChartCard
          heading="NPAT &amp; ROE"
          currencyDenomination={currencyDenomination}
        >
          <ResultsBarChart
            data={data}
            teams={teams}
            formatter={(val: number) => thousandSeparator(round(val, 0))}
            minYAxisvalue={minValue}
            maxYAxisvalue={maxValue}
            numberOfTicks={numberOfTicks}
          />
        </ChartCard>
      </VerticalGroup>
    </RoundResultsContainer>
  );
};

export default RoundResultsNPATRoe;
